import React from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  Tooltip, 
  Divider,
  useTheme,
  alpha,
  Badge,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavigationItems } from './NavigationItems';
import { useMessageStore } from '../../store/messageStore';
import { useCaseStore } from '../../store/caseStore';
import { useAuthStore } from '../../store/authStore';

interface SideNavProps {
  isCollapsed: boolean;
  onCollapse: () => void;
  onNavigate: (path: string) => void;
}

export const SideNav: React.FC<SideNavProps> = ({
  isCollapsed,
  onCollapse,
  onNavigate,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const unreadCount = useMessageStore(state => 
    state.conversations.reduce((sum, conv) => sum + conv.unreadCount, 0)
  );
  const { user } = useAuthStore();
  const cases = useCaseStore(state => state.cases);

  // Calculate pending tasks for current user in active stages
  const pendingTasksCount = React.useMemo(() => {
    if (!user) return 0;
    return cases.reduce((count, case_) => {
      // Only count tasks from the current stage
      const userTasks = case_.tasks.filter(task => 
        task.assignedTo === user.id && 
        task.status !== 'completed' &&
        new Date(task.dueDate) > new Date() &&
        task.stageId === case_.currentStageId // Only include tasks from current stage
      );
      return count + userTasks.length;
    }, 0);
  }, [cases, user]);

  return (
    <>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          p: 1,
        }}
      >
        <IconButton 
          onClick={onCollapse} 
          size="small"
          sx={{
            width: 28,
            height: 28,
          }}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <List sx={{ px: 1 }}>
        {NavigationItems.map((item) => {
          const isActive = location.pathname === item.path;
          const showMessagesBadge = item.path === '/messages' && unreadCount > 0;
          const showCasesBadge = item.path === '/cases' && pendingTasksCount > 0;

          const itemContent = (
            <>
              <ListItemIcon 
                sx={{ 
                  minWidth: 0,
                  mr: isCollapsed ? 0 : 2,
                  justifyContent: 'center',
                  color: isActive ? 'primary.main' : 'text.secondary',
                }}
              >
                {showMessagesBadge || showCasesBadge ? (
                  <Badge
                    badgeContent={showMessagesBadge ? unreadCount : pendingTasksCount}
                    color={showMessagesBadge ? 'error' : 'warning'}
                  >
                    {item.icon}
                  </Badge>
                ) : item.icon}
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'primary.main' : 'text.primary',
                  }}
                  sx={{ flex: 'auto' }}
                />
              )}
            </>
          );

          return (
            <ListItem 
              button 
              key={item.text}
              onClick={() => onNavigate(item.path)}
              sx={{
                minHeight: 44,
                width: 'auto',
                borderRadius: 1.5,
                justifyContent: 'center',
                ...(isCollapsed ? {
                  px: 1.25,
                  mx: 0.75,
                } : {
                  px: 2,
                }),
                background: isActive ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.08 : 0.15) : 'transparent',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.2),
                },
              }}
            >
              {isCollapsed ? (
                <Tooltip title={item.text} placement="right" arrow>
                  {itemContent}
                </Tooltip>
              ) : (
                itemContent
              )}
            </ListItem>
          );
        })}
      </List>
    </>
  );
};